import React, {useEffect, useState} from 'react';
import MessageBroker from '@bonprix/pattern-library/components/message-broker';
import ProductMessages from '../message-list';
import CoreMessages from '@core/scripts/components/messages';
import Messages from '@core/scripts/components/messages';
import Toggle from '@bonprix/pattern-library/templates/toggle/default';
import Select from '@bonprix/pattern-library/templates/input/Select.jsx';
import InputGroup from '@bonprix/pattern-library/templates/input/Group.jsx';
import Device from '@core/scripts/components/device';
import LandmarkAdapter from '@core/scripts/components/landmark-adapter';
import LandmarkHelper from '@core/scripts/helper/landmark-helper';
import NitroEventHelper from '@core/scripts/helper/nitro-event-helper';
import AvailabilityStatusMapper from '../../helper/availability-status-mapper';
import NitroEventFactory from '../../tracking/nitro-event-factory';
import MiniSlider from '@bonprix/pattern-library/templates/slider/MiniSlider.jsx';
import Slide from '@bonprix/pattern-library/templates/slider/Slide.jsx';
import Option from '@bonprix/pattern-library/templates/input/Option.jsx';
import isBeyondEdgeSlidingEnabled from './selection-utils';
import {SliderTypes} from '../recommendations/slider-types';
import {Typography} from '@bonprix/design-system';
import {BenefitSectionItemAttributes} from "../../features/benefitsection/benefit-section-item-attribute.ts";

export default function StyleSelection({texts, product, currentItem, currentItemOption, currentStyle, fullWidth}) {
    const [forceUpdate, setForceUpdate] = useState(false);
    const [beyondEdgeSliding, setBeyondEdgeSliding] = useState(isBeyondEdgeSlidingEnabled());

    useEffect(() => {
        const subscription = MessageBroker.subscribe(Messages.BREAKPOINT.CHANGED, () =>
            setBeyondEdgeSliding(isBeyondEdgeSlidingEnabled())
        );
        return () => MessageBroker.unsubscribe(subscription);
    }, []);

    const sendSwipeLandmark = () => LandmarkAdapter.sendClickLandmark('adp_variant_slide');
    const shouldRenderSelection = () => {
        return product.styles.length > 1;
    };

    const getActiveIndex = (styles, currentStyle) => {
        let activeStyleIndex = 0;
        styles.find((item, index) => {
            if (currentStyle?.id === item.id) {
                activeStyleIndex = index;
            }
        });
        return activeStyleIndex;
    };

    const handleBenefitItemAttributes = (newItemOption, newItem) => {
        const attributes = [];
        if (newItemOption) {
            if (!newItemOption.parcelShopDelivery) {
                attributes.push(BenefitSectionItemAttributes.PARCEL_SHOP_DELIVERY);
            }
        } else {
            if (newItem?.itemOptions.some(option => !option.parcelShopDelivery)) {
                attributes.push(BenefitSectionItemAttributes.PARCEL_SHOP_DELIVERY);
            }
        }
        MessageBroker.publish(ProductMessages.BENEFIT_ITEM_HIDE_IF_MISSING, {attributes});
    };

    const onChangeStyle = (style) => {
        setForceUpdate(false);

        if (currentStyle?.id === style.id) {
            return;
        }

        const previousItem = currentItem;
        const previousItemOption = currentItemOption;

        let newItem = null;
        let newItemOption = null;

        if (previousItem) {
            newItem = style.items.find((item) => item.name === previousItem.name) || style.items[0];
            MessageBroker.publish(CoreMessages.RECOMMENDATIONS.ITEM_CHANGED, {
                itemNumber: newItem.itemNumber,
                types: [SliderTypes.DEFAULT, SliderTypes.COMPLEMENTARY_ARTICLES, SliderTypes.OUTFIT],
            });
        }

        if (previousItemOption && newItem) {
            newItemOption =
                newItem.itemOptions.find((itemOption) => itemOption.name === previousItemOption.name) || null;
        }

        MessageBroker.publish(ProductMessages.CHANGED, {style, item: newItem, itemOption: newItemOption});

        handleBenefitItemAttributes(newItemOption, newItem);

        NitroEventHelper.trigger(
            NitroEventHelper.EventType.DISPLAY_PRODUCT_DETAILS,
            NitroEventFactory.createDisplayProductDetailsEvent(product, style, newItem, newItemOption)
        );
        sendVariantLandmark(style, newItem, newItemOption);
        MessageBroker.publish(CoreMessages.WISHLIST_ITEM_CHANGE, {productId: product.styleId, itemId: newItem.id});
    };

    const sendVariantLandmark = (style, item, itemOption) => {
        const landmarkData = {
            siteType: 'adp',
            selectionType: 'variant',
            selection: style.name,
            itemNumber: item.itemNumber + item.promotion,
            availability: AvailabilityStatusMapper.mapToAvailabilityStatus(style.availability),
            channelId: product.channelId,
            shopId: product.shopId,
            masterStyleId: product.masterStyleId,
            digiStyleId: product.styleId,
            itemId: item.id,
        };

        if (itemOption) {
            landmarkData.itemOptionId = itemOption.id;
        }

        LandmarkAdapter.sendClickLandmark(LandmarkHelper.createLandmarkDataString(landmarkData));
    };

    const renderSelectionInput = (product, currentStyle) => {
        const styles = product.styles;
        if (product.hasLongStyleName) {
            const getStyleNameWithAvailability = (style) => {
                const name = style.name;
                if (style.availability?.soldOut || style.availability?.tempNotAvailable) {
                    return `${name} (${style.availability?.text})`;
                }
                return name;
            };

            const currentStyleName = getStyleNameWithAvailability(currentStyle);

            return (
                <InputGroup
                    hasRows // muss gesetzt sein, da sonst ein <label> tag eingefügt wird, das bei Interaktion mit den options einen Click auf das <input> Element triggert
                    className={ fullWidth ? "product__variants-select__full_width" : "product__variants-select"}
                    disabled={product.singleStyle}
                >
                    {styles.map((style) => (
                        <a href={style.url} onClick={(e) => e.preventDefault()} />
                    ))}
                    <Select
                        custom
                        value={currentStyle?.id}
                        textValue={currentStyleName}
                        disabled={product.singleStyle}
                        activeOption={forceUpdate ? currentStyle : null}
                    >
                        {styles.map((style) => {
                            const name = getStyleNameWithAvailability(style);

                            return (
                                <Option
                                    custom
                                    value={style.id}
                                    onClick={() => onChangeStyle(style)}
                                    icon={style.availability?.tempNotAvailable ? 'email' : null}
                                    disabled={style.availability?.soldOut}
                                >
                                    <a href={style.url} onClick={(e) => e.preventDefault()} />
                                    {name}
                                </Option>
                            );
                        })}
                    </Select>
                </InputGroup>
            );
        }

        const activeIndex = getActiveIndex(product.styles, currentStyle);

        return (
            <MiniSlider
                className={fullWidth ? "product-variants-slider__full_width" : "product-variants-slider"}
                isTouch={Device.isTouch() || Device.isApp()}
                activeSlideIndex={beyondEdgeSliding ? activeIndex + 1 : activeIndex}
                onPrevButtonClick={sendSwipeLandmark}
                onNextButtonClick={sendSwipeLandmark}
                onFirstSlide={sendSwipeLandmark}
                beyondEdgeSliding={beyondEdgeSliding}
            >
                {styles.map((style) => {
                    return (
                        <Slide key={style.id}>
                            <a href={style.url} onClick={(e) => e.preventDefault()}>
                                <Toggle
                                    className="product__variant"
                                    active={currentStyle && currentStyle.id === style.id}
                                    email={style.availability?.tempNotAvailable ?? false}
                                    strikeThrough={style.availability?.soldOut ?? false}
                                    text={style.name}
                                    onClick={() => onChangeStyle(style)}
                                />
                            </a>
                        </Slide>
                    );
                })}
            </MiniSlider>
        );
    };

    const calculateStyleNameToDisplay = () => {
        if (product.styles.length === 1) {
            return currentStyle.name;
        }
        return '';
    };

    const isError = currentStyle?.availability?.soldOut;

    return (
        <div className="product__variants">
            <span className={fullWidth ? "product__variant-selection__full_width" : "product__variant-selection"}>
                <Typography variant="body1" component="span" color="text-on-background-mid">
                    {texts.ada12_orderitem_variant_label}&nbsp;
                </Typography>
                <Typography variant="body1" component="span">
                    {calculateStyleNameToDisplay()}
                </Typography>
            </span>
            {shouldRenderSelection() && renderSelectionInput(product, currentStyle)}
        </div>
    );
}
